import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { FiSearch, FiX, FiChevronDown, FiCheckSquare, FiHelpCircle, FiRefreshCw } from 'react-icons/fi';

/**
 * BISingleSelectSearch
 * Componente unificado de seleção única com busca no padrão visual dos filtros de BI.
 * Fecha automaticamente ao selecionar uma opção e suporta limpeza rápida com ×.
 */
export default function BISingleSelectSearch({
  label,
  placeholder = 'Selecionar opção...',
  value,
  onChange,
  options = null,
  fetchData = null,
  badgeColor = '#3b82f6',
  showCodePrefix = false,
  formatLabel = null,
  getOptionLabel = null,
  getOptionValue = null,
  required = false,
  style = {}
}) {
  const containerRef = useRef(null);
  const searchInputRef = useRef(null);

  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [asyncOptions, setAsyncOptions] = useState(options || []);
  const [loading, setLoading] = useState(false);
  const [hasLoadedInitial, setHasLoadedInitial] = useState(false);
  const lastLoadedSearchRef = useRef(null);

  const stringVal = value !== null && value !== undefined ? String(value).trim() : '';

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isOpen) {
        setIsOpen(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 50);
    }
  }, [isOpen]);

  const extractItemsFromResponse = (response) => {
    if (!response) return [];
    if (Array.isArray(response)) return response;
    const target = response.data !== undefined ? response.data : response;
    if (Array.isArray(target)) return target;
    if (typeof target === 'object' && target !== null) {
      for (const key of Object.keys(target)) {
        if (Array.isArray(target[key])) {
          return target[key];
        }
      }
    }
    return [];
  };

  const loadAsyncOptions = useCallback(
    async (term = '') => {
      if (!fetchData) return;
      try {
        setLoading(true);
        const response = await fetchData(term, 1000, 0);
        const items = extractItemsFromResponse(response);
        setAsyncOptions(items);
      } catch (err) {
        console.error('Erro ao carregar opções assíncronas:', err);
      } finally {
        setLoading(false);
      }
    },
    [fetchData]
  );

  // Sempre que abrir o dropdown, consulta a API para trazer registros mais recentes
  useEffect(() => {
    if (fetchData && isOpen) {
      lastLoadedSearchRef.current = searchTerm;
      loadAsyncOptions(searchTerm);
      setHasLoadedInitial(true);
    }
  }, [fetchData, isOpen, searchTerm, loadAsyncOptions]);

  // Busca debounced ao digitar no input
  useEffect(() => {
    if (!fetchData || !isOpen) return;
    const timer = setTimeout(() => {
      if (lastLoadedSearchRef.current !== searchTerm) {
        lastLoadedSearchRef.current = searchTerm;
        loadAsyncOptions(searchTerm);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm, fetchData, isOpen, loadAsyncOptions]);

  // Se houver valor selecionado mas ainda não carregou as opções, busca na API
  useEffect(() => {
    if (!fetchData || !stringVal || hasLoadedInitial) return;
    loadAsyncOptions('');
    setHasLoadedInitial(true);
  }, [fetchData, stringVal, hasLoadedInitial, loadAsyncOptions]);

  const rawOptions = useMemo(() => {
    if (fetchData) {
      return asyncOptions.length > 0 || hasLoadedInitial ? asyncOptions : (options || []);
    }
    return options !== null ? options : asyncOptions;
  }, [fetchData, asyncOptions, hasLoadedInitial, options]);

  const extractVal = useCallback(
    (opt) => {
      if (!opt && opt !== 0) return '';
      if (getOptionValue) return String(getOptionValue(opt));
      if (opt.codigo !== undefined && opt.codigo !== null) return String(opt.codigo);
      if (opt.codprod !== undefined && opt.codprod !== null) return String(opt.codprod);
      if (opt.codfunc !== undefined && opt.codfunc !== null) return String(opt.codfunc);
      if (opt.id !== undefined && opt.id !== null) return String(opt.id);
      if (opt.code !== undefined && opt.code !== null) return String(opt.code);
      return String(opt);
    },
    [getOptionValue]
  );

  const extractLabel = useCallback(
    (opt) => {
      if (!opt && opt !== 0) return '';
      if (formatLabel) return formatLabel(opt);
      if (getOptionLabel) return getOptionLabel(opt);
      const name =
        opt.label ||
        opt.nomefantasia ||
        opt.nome ||
        opt.razaosocial ||
        opt.descricao ||
        opt.name ||
        '';
      const code =
        opt.codigo !== undefined
          ? opt.codigo
          : opt.codprod !== undefined
          ? opt.codprod
          : opt.codfunc !== undefined
          ? opt.codfunc
          : opt.id !== undefined
          ? opt.id
          : '';

      if (showCodePrefix && code) {
        return `#${code} - ${name || `Cód. ${code}`}`;
      }
      return name || (code ? `Cód. ${code}` : String(opt));
    },
    [formatLabel, getOptionLabel, showCodePrefix]
  );

  const selectedItem = useMemo(() => {
    if (!stringVal) return null;
    return rawOptions.find((opt) => extractVal(opt) === stringVal) || null;
  }, [rawOptions, stringVal, extractVal]);

  const filteredOptions = useMemo(() => {
    if (!searchTerm.trim()) return rawOptions;
    if (fetchData) {
      const q = searchTerm.toLowerCase().trim();
      const localMatches = rawOptions.filter((opt) => {
        const labelStr = extractLabel(opt).toLowerCase();
        const valStr = extractVal(opt).toLowerCase();
        return labelStr.includes(q) || valStr.includes(q);
      });
      return localMatches.length > 0 ? localMatches : rawOptions;
    }
    const q = searchTerm.toLowerCase().trim();
    return rawOptions.filter((opt) => {
      const labelStr = extractLabel(opt).toLowerCase();
      const valStr = extractVal(opt).toLowerCase();
      return labelStr.includes(q) || valStr.includes(q);
    });
  }, [rawOptions, searchTerm, extractLabel, extractVal, fetchData]);

  const handleSelect = (opt) => {
    const val = extractVal(opt);
    onChange(val, opt);
    setSearchTerm('');
    setIsOpen(false);
  };

  const handleClear = (e) => {
    if (e) e.stopPropagation();
    onChange('', null);
    setSearchTerm('');
  };

  const displayTriggerText = selectedItem
    ? extractLabel(selectedItem)
    : stringVal ? (loading ? 'Carregando...' : `Opção #${stringVal}`) : '';

  return (
    <div
      className="form-group-modern mb-0"
      style={{
        position: 'relative',
        width: '100%',
        minWidth: 0,
        zIndex: isOpen ? 1060 : 'auto',
        ...style
      }}
      ref={containerRef}
    >
      {label && (
        <label className="d-flex justify-content-between align-items-center mb-1">
          <span>
            {label} {required && <span className="text-danger">*</span>}
          </span>
          {stringVal && (
            <span
              className="text-muted"
              style={{ fontSize: '0.72rem', cursor: 'pointer' }}
              onClick={handleClear}
              title="Limpar seleção"
            >
              Limpar
            </span>
          )}
        </label>
      )}

      <div className="bi-search-dropdown-container">
        <div className="bi-search-input-wrapper">
          <input
            type="text"
            readOnly
            className={`form-control-modern bi-search-input-box ${stringVal ? 'is-selected' : ''}`}
            placeholder={placeholder}
            value={displayTriggerText}
            onClick={() => setIsOpen(!isOpen)}
          />

          <div className="bi-search-actions-inside">
            {stringVal && (
              <span
                className="bi-selected-badge"
                style={{
                  backgroundColor: `${badgeColor}15`,
                  borderColor: `${badgeColor}40`,
                  color: badgeColor
                }}
              >
                <FiCheckSquare size={12} />
              </span>
            )}
            {stringVal && (
              <span
                className="bi-search-clear-icon"
                title="Limpar seleção"
                onClick={handleClear}
              >
                <FiX size={15} />
              </span>
            )}
            <span
              className={`bi-search-chevron ${isOpen ? 'open' : ''}`}
              onClick={() => setIsOpen(!isOpen)}
              title={isOpen ? 'Fechar lista' : 'Abrir lista'}
            >
              <FiChevronDown size={16} />
            </span>
          </div>
        </div>

        {/* Modal Suspenso (Popover) */}
        {isOpen && (
          <div className="bi-search-popover">
            {/* Barra de Busca Interna */}
            <div className="bi-popover-search-bar">
              <FiSearch size={14} className="bi-popover-search-icon" />
              <input
                ref={searchInputRef}
                type="text"
                className="bi-popover-search-input"
                placeholder={placeholder || 'Buscar opções...'}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') setIsOpen(false);
                }}
              />
            </div>

            {/* Cabeçalho do Popover */}
            <div className="bi-popover-header">
              <span className="bi-popover-meta-title">
                Opções Disponíveis
              </span>
              <div className="bi-popover-header-actions">
                {fetchData && (
                  <button
                    type="button"
                    className="bi-popover-clear-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      lastLoadedSearchRef.current = searchTerm;
                      loadAsyncOptions(searchTerm);
                    }}
                    title="Recarregar opções da API"
                    style={{
                      color: '#475569',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '4px'
                    }}
                  >
                    <FiRefreshCw size={11} className={loading ? 'spin-ico text-primary' : ''} />
                    <span>Recarregar</span>
                  </button>
                )}
                {stringVal && (
                  <button
                    type="button"
                    className="bi-popover-clear-btn"
                    onClick={handleClear}
                  >
                    Desmarcar
                  </button>
                )}
                <span className="bi-popover-count-tag">
                  {filteredOptions.length} / {rawOptions.length}
                </span>
              </div>
            </div>

            {/* Lista de Opções */}
            <div className="bi-popover-list">
              {loading && rawOptions.length === 0 ? (
                <div className="bi-popover-empty">
                  <FiRefreshCw size={18} className="spin-ico d-block mx-auto mb-2 text-primary" />
                  Carregando opções...
                </div>
              ) : filteredOptions.length === 0 ? (
                <div className="bi-popover-empty">
                  <FiHelpCircle size={22} className="d-block mx-auto mb-1 opacity-50" />
                  Nenhum resultado encontrado{searchTerm ? ` para "${searchTerm}"` : ''}.
                </div>
              ) : (
                filteredOptions.map((opt, idx) => {
                  const val = extractVal(opt);
                  const isSelected = val === stringVal;
                  const labelText = extractLabel(opt);
                  const initials = (labelText || 'OP')
                    .replace(/[^a-zA-Z0-9]/g, '')
                    .substring(0, 2)
                    .toUpperCase();

                  return (
                    <div
                      key={`${val}-${idx}`}
                      className={`bi-popover-item ${isSelected ? 'selected' : ''}`}
                      onClick={() => handleSelect(opt)}
                    >
                      <div className="bi-popover-item-left">
                        <div
                          className="bi-popover-avatar"
                          style={{
                            background: isSelected
                              ? `linear-gradient(135deg, ${badgeColor}, #1d4ed8)`
                              : '#e2e8f0',
                            color: isSelected ? '#ffffff' : '#64748b'
                          }}
                        >
                          {initials}
                        </div>
                        <div className="bi-popover-info">
                          <span className="bi-popover-name" title={labelText}>
                            {labelText}
                          </span>
                          {(opt.codigo || opt.codfilial || opt.id) && (
                            <div className="bi-popover-meta">
                              {opt.codigo ? <span>Cód: #{opt.codigo}</span> : (opt.id ? <span>ID: #{opt.id}</span> : null)}
                              {opt.codfilial && <span>• Filial {opt.codfilial}</span>}
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="bi-popover-item-right">
                        {isSelected ? (
                          <span
                            className="d-flex align-items-center gap-1 fw-bold"
                            style={{ fontSize: '0.74rem', color: '#16a34a' }}
                          >
                            <FiCheckSquare size={14} /> Selecionado
                          </span>
                        ) : (
                          val && <span className="bi-popover-target-val">#{val}</span>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
